import {
  BadgeIcon,
  ContactIcon,
  HashIcon,
  MailIcon,
  PhoneIcon,
  RefreshCwIcon,
  UsersIcon,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import type {
  AccountDTO,
  CreateAccountDTO,
  UpdateAccountDTO,
} from '@invenco/common-interface/accounts';

import { Button } from '@/components/core/button';
import { DetailGroup } from '@/components/detail-group';
import { DetailEditButton, DetailList } from '@/components/detail-list';
import { FormDialog } from '@/components/forms/form-dialog';
import { Header, HeaderActions, HeaderIcon, HeaderTitle } from '@/components/header';
import { DetailMain, DetailSide, Page, PageContent } from '@/components/layout';
import { DATA_POLLING_DURATION_MS } from '@/constants';
import { useGateways } from '@/gateways/GatewayProvider';
import { useEntityDetailsQuery, useGatewayMutation } from '@/shared/hooks/queries';
import { usePulseFlag } from '@/shared/hooks/use-pulse-flag';
import { useMessages } from '@/shared/providers/MessagesProvider';

import { AccountDetailsDialogContent } from './account-details-dialog-content';
import { BillingHoldDetail } from './billing-hold-detail';
import { LocationsCard } from './locations-card';
import { ServiceAgreementsCard } from './service-agreements-card';
import { UsersCard } from './users-card';

type Props = {
  isNew?: boolean;
};

export function AccountDetails({ isNew: isNewProp }: Props) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { accountsGateway } = useGateways();
  const messages = useMessages();
  const [isPolling, triggerPolling] = usePulseFlag(DATA_POLLING_DURATION_MS);
  const isNew = isNewProp ?? id === 'new';
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(isNew);

  useEffect(() => {
    if (isNew) {
      setIsDetailsDialogOpen(true);
    }
  }, [isNew]);

  const { data, isLoading, refetch } = useEntityDetailsQuery<AccountDTO>({
    parentKey: 'accounts',
    id,
    query: (accountId, { signal }) => accountsGateway.getAccount(accountId, { signal }),
    isNew,
    refetchInterval: isPolling ? 1000 : false,
  });

  const { mutate: createAccountMutation } = useGatewayMutation({
    mutationFn: (payload: CreateAccountDTO) => accountsGateway.createAccount(payload),
    onSuccess: (newAccount) => {
      messages.success('Account created', { name: newAccount.name });
      void navigate(`/accounts/${newAccount.id}`);
    },
    linkedQuery: ['accounts', id],
  });

  const { mutate: updateAccountMutation } = useGatewayMutation({
    mutationFn: (payload: UpdateAccountDTO) => accountsGateway.updateAccount(id!, payload),
    onSuccess: (updatedAccount) => {
      messages.success('Account updated', { name: updatedAccount.name });
    },
    linkedQuery: ['accounts', id],
  });
  const account: Partial<AccountDTO> = data ?? {};
  const headerTitle = isNew ? 'New account' : (account.name ?? 'Account details');

  return (
    <Page>
      <Header loading={isLoading}>
        <HeaderIcon parentUrl="/accounts" parentTitle="Accounts">
          <UsersIcon />
        </HeaderIcon>
        <HeaderTitle>{headerTitle}</HeaderTitle>
        <HeaderActions>
          {!isNew ? (
            <Button
              size="icon"
              variant="outline"
              aria-label="Refresh"
              onClick={() => void refetch()}
            >
              <RefreshCwIcon />
            </Button>
          ) : null}
        </HeaderActions>
      </Header>
      <PageContent variant="split">
        <DetailMain>
          <UsersCard account={account} isLoading={isLoading} isNew={isNew} />
          <ServiceAgreementsCard account={account} isLoading={isLoading} isNew={isNew} />
          <LocationsCard
            account={account}
            isLoading={isLoading}
            isNew={isNew}
            onInviteComplete={triggerPolling}
          />
        </DetailMain>
        <DetailSide>
          <DetailGroup>
            <DetailList
              title="Account details"
              isLoading={isLoading}
              action={
                <DetailEditButton
                  disabled={isLoading}
                  onClick={() => setIsDetailsDialogOpen(true)}
                />
              }
              items={[
                { icon: HashIcon, label: 'Code', value: account.code },
                { icon: BadgeIcon, label: 'Billing ID', value: account.externalId },
              ]}
            />
            <DetailList
              title="Primary contact"
              isLoading={isLoading}
              items={[
                { icon: ContactIcon, label: 'Name', value: account.primaryName },
                { icon: MailIcon, label: 'Email', value: account.primaryEmail },
                { icon: PhoneIcon, label: 'Phone', value: account.primaryPhone },
              ]}
            />
            <BillingHoldDetail account={account} isLoading={isLoading} />
          </DetailGroup>
        </DetailSide>
      </PageContent>
      <FormDialog open={isDetailsDialogOpen} onOpenChange={setIsDetailsDialogOpen}>
        <AccountDetailsDialogContent
          account={account}
          isNew={isNew}
          onSubmit={async (formData) => {
            if (isNew) {
              return createAccountMutation(formData as CreateAccountDTO);
            }

            return updateAccountMutation({
              name: formData.name,
              externalId: formData.externalId,
              primaryName: formData.primaryName,
              primaryEmail: formData.primaryEmail,
              primaryPhone: formData.primaryPhone,
            });
          }}
        />
      </FormDialog>
    </Page>
  );
}
